<script lang="ts">
export default {
  name: 'CarrierShareOfWalletTable',
};
</script>

<script setup lang="ts">
import { carrierService } from '@/services/api';
import { CarrierShareOfWallet } from '@/types/Carrier';
import { compactMoney } from '@/utilities/Money';
import { onBeforeMount, ref, computed } from 'vue';

const props = defineProps<{
  insurerGroupId: number;
  carrierIds: number[];
  agencyTypes?: string[];
  coverageTypes?: string[];
  departments?: string[];
}>();

const title = 'Carrier Share of Wallet';

const emit = defineEmits(['loaded']);

// -----
// refs
// -----
const tableDataType = ref<'TTM' | 'YTD'>('TTM');
const shareOfWalletData = ref<CarrierShareOfWallet[]>([]);
const loading = ref<boolean>(true);

// ---------------
// computed props
// ---------------
const carrierName = computed(() => shareOfWalletData.value[0]?.carrierName ?? '');
const dataTypeLabel = computed(() => tableDataType.value);
const headers = computed(() => {
  return [
    'Coverage',
    `${dataTypeLabel.value} ${carrierName.value} Premium (% Share of Book)`,
    `${dataTypeLabel.value} CACS Premium`,
    `${dataTypeLabel.value} ${carrierName.value} Client / Policy Count`,
    `Prior ${dataTypeLabel.value} ${carrierName.value} Premium (% Share of Book)`,
    `Prior ${dataTypeLabel.value} CACS Premium`,
    `Prior ${dataTypeLabel.value} ${carrierName.value} Client / Policy Count`,
  ];
});

const data = computed(() => {
  if (shareOfWalletData.value.length === 0) {
    return [];
  }

  // sort by either TTM or YTD premium
  let data = shareOfWalletData.value
    .filter((d) => d.dataType === tableDataType.value)
    .sort((a, b) => {
      const aPremium = tableDataType.value === 'TTM' ? a.carrierPremiumTTM : a.carrierPremiumYTD;
      const bPremium = tableDataType.value === 'TTM' ? b.carrierPremiumTTM : b.carrierPremiumYTD;
      return bPremium - aPremium;
    });

  // add the "total" row back
  data = [...data, shareOfWalletData.value.find((d) => d.coverageType === 'TOTAL')] as CarrierShareOfWallet[];

  return data;
});

// --------
// methods
// --------
function coverageTypeLabel(coverageType: string): string {
  return coverageType === 'TOTAL' ? 'ALL COVERAGES' : coverageType;
}

function carrierPremium(wallet: CarrierShareOfWallet): string {
  const carrierPremium = tableDataType.value === 'TTM' ? wallet.carrierPremiumTTM : wallet.carrierPremiumYTD;
  const premium = tableDataType.value === 'TTM' ? wallet.totalPremiumTTM : wallet.totalPremiumYTD;
  if (carrierPremium > 0 && premium > 0) {
    return `${compactMoney(carrierPremium)} (${((carrierPremium / premium) * 100).toFixed(2)}%)`;
  }
  return `${compactMoney(carrierPremium)}`;
}

function premium(wallet: CarrierShareOfWallet): string {
  const premium = tableDataType.value === 'TTM' ? wallet.totalPremiumTTM : wallet.totalPremiumYTD;
  return compactMoney(premium);
}

function clientPolicyCount(wallet: CarrierShareOfWallet): string {
  const totalClients = tableDataType.value === 'TTM' ? wallet.carrierClientsTTM : wallet.carrierClientsYTD;
  const totalPolicies = tableDataType.value === 'TTM' ? wallet.carrierPoliciesTTM : wallet.carrierPoliciesYTD;
  return `${totalClients} / ${totalPolicies}`;
}

function priorCarrierPremium(wallet: CarrierShareOfWallet): string {
  const carrierPremium =
    tableDataType.value === 'TTM' ? wallet.carrierPremiumPreviousTTM : wallet.carrierPremiumPreviousYTD;
  const premium = tableDataType.value === 'TTM' ? wallet.totalPremiumPreviousTTM : wallet.totalPremiumPreviousYTD;
  if (carrierPremium > 0 && premium > 0) {
    return `${compactMoney(carrierPremium)} (${((carrierPremium / premium) * 100).toFixed(2)}%)`;
  }
  return compactMoney(carrierPremium);
}

function priorPremium(wallet: CarrierShareOfWallet): string {
  const premium = tableDataType.value === 'TTM' ? wallet.totalPremiumPreviousTTM : wallet.totalPremiumPreviousYTD;
  return compactMoney(premium);
}

function priorClientPolicyCount(wallet: CarrierShareOfWallet): string {
  const totalClients = tableDataType.value === 'TTM' ? wallet.carrierClientsPreviousTTM : wallet.carrierClientsPreviousYTD;
  const totalPolicies =
    tableDataType.value === 'TTM' ? wallet.carrierPoliciesPreviousTTM : wallet.carrierPoliciesPreviousYTD;
  return `${totalClients} / ${totalPolicies}`;
}

// ---------
// handlers
// ---------

function changeTableDataType(type: 'TTM' | 'YTD'): void {
  tableDataType.value = type;
}

// ------------
// defineExpose
// ------------

defineExpose({
  changeTableDataType,
});

onBeforeMount(async () => {
  shareOfWalletData.value = await carrierService.shareOfWallet(
    props.insurerGroupId,
    props.carrierIds,
    props.agencyTypes,
    props.coverageTypes,
    props.departments,
  );
  loading.value = false;
  emit('loaded');
});
</script>

<template>
  <div class="flex flex-col m-4 items-center" :class="{ 'hidden': !shareOfWalletData.length }">
    <div class="max-w-(--breakpoint-2xl)">
      <div class="flex m-2 justify-center gap-4">
        <h2 class="text-base font-bold text-[#0d0d0d] font-roboto text-[16px] text-left">{{ title }}</h2>
        <div class="flex border border-solid border-black rounded-xl">
          <button id="ttmButton"
            class="px-3 rounded-l-xl transition"
            :class="tableDataType === 'TTM' && 'bg-primary text-white font-bold'"
            @click="changeTableDataType('TTM')"
          >
            TTM
          </button>
          <button id="ytdButton"
            class="px-3 rounded-r-xl transition"
            :class="tableDataType === 'YTD' && 'bg-primary text-white font-bold'"
            @click="changeTableDataType('YTD')"
          >
            YTD
          </button>
        </div>
      </div>
      <div>
        <table class="apptable whitespace-normal dense --tight-first text-[0.8rem]" id="shareOfWalletTable">
          <thead class="text-[0.8rem]">
            <tr>
              <td v-for="header in headers" :key="header" class="nosort p-4">{{ header }}</td>
            </tr>
          </thead>
          <tbody>
            <tr
                v-for="wallet in data.filter((wallet) => wallet.coverageType !== 'TOTAL')"
                :key="wallet.coverageType"
            >
              <td class="p-4">{{ coverageTypeLabel(wallet.coverageType) }}</td>
              <td class="p-4">{{ carrierPremium(wallet) }}</td>
              <td class="p-4">{{ premium(wallet) }}</td>
              <td class="p-4">{{ clientPolicyCount(wallet) }}</td>
              <td class="p-4">{{ priorCarrierPremium(wallet) }}</td>
              <td class="p-4">{{ priorPremium(wallet) }}</td>
              <td class="p-4">{{ priorClientPolicyCount(wallet) }}</td>
            </tr>

            <tr
                v-for="wallet in data.filter((wallet) => wallet.coverageType == 'TOTAL')"
                :key="wallet.coverageType"
                class="font-bold"
            >
              <td class="p-4">{{ coverageTypeLabel(wallet.coverageType) }}</td>
              <td class="p-4">{{ carrierPremium(wallet) }}</td>
              <td class="p-4">{{ premium(wallet) }}</td>
              <td class="p-4">{{ clientPolicyCount(wallet) }}</td>
              <td class="p-4">{{ priorCarrierPremium(wallet) }}</td>
              <td class="p-4">{{ priorPremium(wallet) }}</td>
              <td class="p-4">{{ priorClientPolicyCount(wallet) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
